import { showError } from '@calendly/alert'
import { useFlag } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { Segment } from '@calendly/segment'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch } from 'react-redux'

import { historyPush } from 'actions/core/navigation'
import { useCreateEventTypeMutation } from 'features/event_type_editor/api'
import { useGetDefaultAvailabilityRules } from 'features/event_type_editor/utils'
import { DEFAULT_TAB_KEY, EVENT_TYPE_EDITOR_CRUD_GATE } from 'features/event_type_editor_crud'
import { useEventType } from 'features/event_type_provider'
import {
  buildNotetakerEtToggleSavedTrackingPayload,
  isNotetakerEtSaveEligibleForLocationConfigurations,
} from 'features/growth_experiments'
import { closeEditPermissionsModal } from 'features/object_level_permissions'
import { useUpsertUserGuideStepMutation } from 'features/user_guide'
import { useTrack } from 'hooks/analytics_events'

import { useEventTypeEditorSidebar } from '../../../hooks'
import { useSyncNotetakerSettings } from '../../hooks'
import { BaseSection } from '../base_section'
import { valuesMapper } from '../utils'
import { getFormatValuesToServerData, getInitialValues, getValidationSchema } from './utils'
import { getEventDetailsPanelKeys } from '../components/event_details/utils'
import { Panels } from '../panels'

export const NewForm = ({ paymentsUpfrontVariant = null }) => {
  const dispatch = useDispatch()
  const eventType = useEventType()
  const defaultAvailabilityRules = useGetDefaultAvailabilityRules()
  const track = useTrack()
  const selectedSectionRef = useRef(null)
  const notetakerEnabledForEtRef = useRef(undefined)
  const hasSeenNotetakerToggleRef = useRef(false)
  const notetakerLocationConfigurationsRef = useRef([])
  const [createEventType, { isLoading, error }] = useCreateEventTypeMutation()
  const [upsertUserGuideStep] = useUpsertUserGuideStepMutation()
  const syncNotetakerSettings = useSyncNotetakerSettings()
  const { groupUuid, replaceSidebar } = useEventTypeEditorSidebar()
  const isCrudEditorEnabled = useFlag(EVENT_TYPE_EDITOR_CRUD_GATE)

  const panelKeys = useMemo(
    () => getEventDetailsPanelKeys({ ...eventType, paymentsUpfrontVariant }),
    [eventType, paymentsUpfrontVariant]
  )

  const { isMultiHost, isTeam, isMETParent, isUser } = eventType
  const isEventTypeWithMemberships = isMultiHost || isTeam

  useEffect(() => {
    Segment.track('et_editor.section.open', {
      section: 'new_event_type'
    })

    return () => dispatch(closeEditPermissionsModal())
  }, [dispatch])

  const hasMsTeamsFlag = useFlag('ms_teams_notetaker')
  const onSuccess = useCallback((newEventType) => {
    const notetakerEnabledForEt = notetakerEnabledForEtRef.current
    const hasSeenNotetakerToggle = hasSeenNotetakerToggleRef.current
    const notetakerLocationConfigurations = notetakerLocationConfigurationsRef.current
    notetakerEnabledForEtRef.current = undefined
    hasSeenNotetakerToggleRef.current = false
    notetakerLocationConfigurationsRef.current = []

    const isNotetakerSaveEligible = isNotetakerEtSaveEligibleForLocationConfigurations(
      notetakerLocationConfigurations,
      hasMsTeamsFlag
    )

    if (typeof notetakerEnabledForEt === 'boolean' && isNotetakerSaveEligible) {
      syncNotetakerSettings({
        eventTypeId: newEventType?.id,
        notetakerEnabledForEt,
      })
    }

    if (hasSeenNotetakerToggle && typeof notetakerEnabledForEt === 'boolean' && isNotetakerSaveEligible) {
      const trackingPayload = buildNotetakerEtToggleSavedTrackingPayload({
        eventTypeId: newEventType?.id,
        flow: 'create',
        isEnabled: notetakerEnabledForEt,
      })

      if (trackingPayload) {
        track(trackingPayload)
      }
    }

    if (!isMETParent) {
      upsertUserGuideStep({ identifier: 'customize_and_share_first_et', status: 'completed' })
    }

    if (isCrudEditorEnabled && !isMultiHost) {
      dispatch(historyPush(`/event_types/${newEventType.id}/editor/${DEFAULT_TAB_KEY}`))
      return
    }

    const isNoPersonalLocation = isUser && !newEventType.location_configurations?.length

    replaceSidebar({
      id: newEventType.id,
      showPreview: true,
      justCreated: true,
      selectedSection: selectedSectionRef?.current,
      ...(isUser ? { defaultOpenKey: isNoPersonalLocation ? 'location' : 'availability' } : {}),
      ...(isMETParent
        ? { ownerType: 'Organization', defaultOpenKey: 'assign' }
        : { ownerId: newEventType?.owner_id || newEventType?.profile?.owner_id || null }
      )
    })
  }, [
    dispatch,
    isCrudEditorEnabled,
    isMETParent,
    isMultiHost,
    replaceSidebar,
    syncNotetakerSettings,
    track,
    upsertUserGuideStep,
    isUser,
    hasMsTeamsFlag
  ])

  const handleSubmit = useCallback((attrs, values) => {
    if (isLoading) return Promise.resolve()

    notetakerEnabledForEtRef.current = values?.notetakerEnabledForEt
    hasSeenNotetakerToggleRef.current = Boolean(values?.hasSeenNotetakerToggle)

    const attributes = (isMETParent && groupUuid) ? { ...attrs, user_group_uuid: groupUuid } : attrs

    if (
      isEventTypeWithMemberships
      && !attributes.selected_member_ids?.length
      && !attributes.selected_invitation_ids?.length
    ) {
      return dispatch(showError(I18n.t('publisher.event_type_editor.sections.errors.section')))
    }

    notetakerLocationConfigurationsRef.current = attributes.location_configurations_attributes || []

    return createEventType({
      eventType,
      attributes,
      onSuccess
    })
  }, [
    createEventType,
    dispatch,
    eventType,
    groupUuid,
    isEventTypeWithMemberships,
    isLoading,
    isMETParent,
    onSuccess,
  ])

  return (
    <BaseSection
      enableReinitialize={false}
      formatValues={getFormatValuesToServerData({ eventType, paymentsUpfrontVariant })}
      initialValues={getInitialValues({ eventType, defaultAvailabilityRules, paymentsUpfrontVariant })}
      validationSchema={getValidationSchema({ eventType, paymentsUpfrontVariant })}
      valuesMapper={valuesMapper}
      externalError={error}
      onSubmit={handleSubmit}
      selectedSectionRef={selectedSectionRef}
    >
      <Panels
        panelKeys={panelKeys}
        panelPropsByKey={{
          payment: {
            trackingSection: 'initial_editor_page'
          }
        }}
      />
    </BaseSection>
  )
}
